import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { formatVenueDateTime, VENUE_TZ } from '@tg-hookah/shared';
import { bookingDtoSchema, type BookingDto } from '@tg-hookah/shared/dto';
import { api } from '@/lib/api';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@/components/Spinner';
import { toast } from '@/components/Toast';
import axios from 'axios';

const listSchema = z.array(bookingDtoSchema);

function useMyBookings() {
  return useQuery({
    queryKey: ['my-bookings'],
    queryFn: async (): Promise<BookingDto[]> => {
      const res = await api.get('/me/bookings');
      return listSchema.parse(res.data);
    },
  });
}

function useCancelBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<BookingDto> => {
      const res = await api.post(`/bookings/${id}/cancel`);
      return bookingDtoSchema.parse(res.data);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['my-bookings'] });
    },
  });
}

/** Bookings are Moscow wall-clock everywhere — bot, admin panel, here — so a
 *  guest abroad reads back exactly the time they booked, not a device-shifted
 *  one. Grouping uses the same timezone or a late-evening booking could land
 *  in the wrong month. */
function monthKey(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    timeZone: VENUE_TZ,
    month: 'long',
    year: 'numeric',
  });
}

export function BookingHistory() {
  const { t } = useTranslation();
  const { data, isLoading } = useMyBookings();
  const cancel = useCancelBooking();

  if (isLoading)
    return (
      <div className="py-6 text-center text-hint">
        <Spinner />
      </div>
    );
  if (!data || data.length === 0)
    return <p className="text-hint text-sm py-4">{t('profile.noBookings')}</p>;

  const groups = new Map<string, BookingDto[]>();
  for (const b of data) {
    const k = monthKey(b.date);
    const list = groups.get(k) ?? [];
    list.push(b);
    groups.set(k, list);
  }

  const onCancel = async (id: string) => {
    if (typeof window !== 'undefined' && !window.confirm('Отменить бронь?')) return;
    try {
      await cancel.mutateAsync(id);
      toast('Бронь отменена', 'success');
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? ((err.response?.data as { message?: string } | undefined)?.message ?? t('common.error'))
        : t('common.error');
      toast(msg, 'error');
    }
  };

  const now = Date.now();
  const isCancellable = (b: BookingDto) =>
    b.status !== 'CANCELLED' && new Date(b.date).getTime() > now;

  return (
    <div className="flex flex-col gap-4">
      {[...groups.entries()].map(([month, items]) => (
        <section key={month}>
          <h3 className="eyebrow mb-2">{month}</h3>
          <ul className="flex flex-col gap-2">
            {items.map((b) => (
              <li
                key={b.id}
                className="rounded-2xl p-3.5"
                style={{
                  background: 'rgba(255,255,255,0.025)',
                  border: '1px solid var(--line-1)',
                }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="font-display font-semibold tracking-tight tabular-nums">
                    {formatVenueDateTime(b.date)}
                  </div>
                  {b.status !== 'NEW' ? (
                    <span
                      className="font-mono text-[9.5px] uppercase tracking-[0.16em] rounded-full px-2 py-0.5 shrink-0"
                      style={{
                        border: '1px solid var(--line-2)',
                        background: 'rgba(255,255,255,0.04)',
                        color: b.status === 'CANCELLED' ? 'var(--ember)' : 'var(--text-1)',
                      }}
                      aria-label={`status ${b.status}`}
                    >
                      {statusRu(b.status)}
                    </span>
                  ) : null}
                </div>
                <div className="font-mono text-[11px] tracking-[0.04em] text-white/45 mt-1">
                  {b.guestsCount} гостей · {b.durationMin} мин
                </div>
                {isCancellable(b) ? (
                  <div className="mt-2.5 flex justify-end">
                    <button
                      type="button"
                      disabled={cancel.isPending}
                      onClick={() => onCancel(b.id)}
                      className="text-[11px] font-mono uppercase tracking-[0.16em] rounded-full px-3 py-1.5 disabled:opacity-60 transition-colors"
                      style={{
                        border: '1px solid rgba(194,58,46,0.45)',
                        background: 'rgba(194,58,46,0.08)',
                        color: 'var(--ember)',
                      }}
                    >
                      Отменить
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function statusRu(s: BookingDto['status']): string {
  switch (s) {
    case 'NEW':
      return 'Новая';
    case 'CONFIRMED':
      return 'Подтверждена';
    case 'CANCELLED':
      return 'Отменена';
    default:
      return s;
  }
}
